import { Injectable } from '@angular/core';
import { AngularFirestore } from '@angular/fire/compat/firestore';
import firebase from 'firebase/compat/app';
import { Observable, combineLatest, of } from 'rxjs';
import { catchError, distinctUntilChanged, map, shareReplay, startWith, switchMap } from 'rxjs/operators';
import { NotificationTypeCatalog, NotificationTypeCatalogItem } from 'src/app/models/notification-type-catalog.model';
import { NewAuthService } from './auth/new-auth-service.service';
import { FirestoreService } from './firestore.service';
import { Notification, NotificationRow } from './models/notification.model';

/** How many rows the panel holds. Everything older is behind "View all notifications". */
const PANEL_LIMIT = 30;

/** Presentation metadata per notification type; see `NotificationTypeCatalog`. */
const NOTIFICATION_TYPES_DOC = 'welby_configuration/notification_types';

@Injectable({ providedIn: 'root' })
export class NotificationsService {
  constructor(private firestoreService: FirestoreService, private auth: NewAuthService, private afs: AngularFirestore) {}

  /**
   * The signed-in user's notifications, newest first, live.
   *
   * Keyed on `owner`, which the writer sets to the patient's assigned nurse. A notification whose
   * owner could not be resolved is written with `owner: null` and is therefore NOT in this stream —
   * it sits in an unassigned queue with nobody watching it. That is a real gap (measured at 8% of
   * patients on dev, who have no care team at all) and it needs a sweep somewhere; this panel is
   * deliberately not that place, because a nurse's own list is the wrong home for work nobody owns.
   *
   * Emits an empty list rather than erroring while the user is still resolving, so the bell renders
   * at zero instead of flashing an error on every page load.
   *
   * **Built once and shared.** `colWithIds$` is cold — it calls `snapshotChanges()` on subscribe and
   * multicasts nothing — so without the `shareReplay` below, every consumer would open its OWN live
   * listener on the same query. That is not theoretical: the shell's badge alone reads this through
   * four separate `| async` bindings, and the panel adds a fifth, so one signed-in user was paying
   * for five listeners on `notifications where owner == uid`. The field is the single stream and
   * `myNotifications$()` just hands it out.
   *
   * `refCount: true` so the listener closes when the last consumer goes away (logout, navigation
   * away from the shell) rather than living for the lifetime of a root-provided singleton.
   */
  private readonly notifications$: Observable<NotificationRow[]> = this.auth.user$.pipe(
    map((user) => user?.user_id ?? null),
    // Without this, any re-emission of the same user tears down the listener and opens a new one.
    distinctUntilChanged(),
    switchMap((uid) => {
      if (!uid) {
        return of([] as NotificationRow[]);
      }

      return combineLatest([
        this.firestoreService.colWithIds$<Notification>('notifications', (ref) =>
          ref.where('owner', '==', uid).orderBy('date', 'desc').limit(PANEL_LIMIT)
        ),
        this.typeCatalog$(),
      ])
        .pipe(
          map(([rows, catalog]) => rows.map((row) => this.toRow(row, catalog))),
          // INSIDE the switchMap, deliberately: an error here kills only this query, and the outer
          // chain survives to re-run on the next user change. Outside, a single failure would
          // complete the stream for the rest of the session — `| async` unsubscribes on error and
          // never retries, so the badge would freeze and the panel would silently stop updating with
          // nothing on screen to say so. The realistic trigger is not exotic: a missing composite
          // index answers this exact query with `failed-precondition`, which is how it first showed
          // up in dev.
          catchError((error) => {
            console.error('notifications: live query failed; the panel will show empty', error);

            return of([] as NotificationRow[]);
          }),
        );
    }),
    shareReplay({ bufferSize: 1, refCount: true }),
  );

  myNotifications$(): Observable<NotificationRow[]> {
    return this.notifications$;
  }

  /**
   * Mark one notification read.
   *
   * Writes both fields together. They are two views of one fact, and a row that carries `read: true`
   * with no `readAt` says the user saw it at no particular time — which is worse than either field
   * on its own, because it looks correct.
   */
  async markRead(notificationId: string): Promise<void> {
    // Writes through AngularFirestore directly, NOT `FirestoreService.update`: that helper appends
    // `lastUpdatedBy` and `updatedAt` of its own, and the security rule on this collection allows a
    // client to touch `read` and `readAt` and nothing else. The extra fields make the write fail the
    // rule, Firestore rejects it, and the SDK rolls the local change back — the row turns grey and
    // then unread again a moment later.
    //
    // Loosening the rule to admit the two extra fields would be the other way round, and worse: they
    // are audit fields nobody reads here, and `hasOnly` is what stops a client rewriting `owner`.
    await this.afs.doc(`notifications/${notificationId}`).update({
      read: true,
      readAt: firebase.firestore.FieldValue.serverTimestamp(),
    });
  }

  /**
   * Mark every unread row in the panel read.
   *
   * Takes the rows the panel is already showing rather than re-querying: the user is acting on what
   * they can see, and a re-query could sweep a notification that arrived in the last second and was
   * never on screen.
   */
  async markAllRead(rows: NotificationRow[]): Promise<void> {
    await Promise.all(rows.filter((row) => !row.read).map((row) => this.markRead(row.id)));
  }

  /**
   * Adds what the list needs and the stored document does not carry.
   *
   * Presentation only — nothing here is written back. `date` arrives as a Firestore Timestamp, which
   * a template cannot group or format on its own.
   */
  private toRow(row: Notification & { id: string }, catalog: Map<string, NotificationTypeCatalogItem>): NotificationRow {
    const date = this.toDate(row.date);
    const entry = catalog.get(row.type);

    return {
      ...row,
      date,
      group: this.groupOf(date),
      initials: entry?.initials || PLACEHOLDER_INITIALS,
      typeLabel: entry?.label,
    };
  }

  /**
   * Presentation metadata per type, from `welby_configuration/notification_types`.
   *
   * `startWith` an empty map, deliberately: `combineLatest` waits for every source, so without it a
   * slow or missing config document would hold back rows that are already fetched and already the
   * user's own. The panel renders immediately with placeholder tiles and upgrades them in place when
   * the catalog lands. Fail-open — the opposite of the canary configs, and for the opposite reason:
   * there the ambiguous state is showing someone a feature, here it is hiding their own notifications.
   *
   * Read INSIDE the per-user stream, like the notifications query itself: the rule on
   * `welby_configuration/{document}` requires `request.auth != null`, so a read issued before there
   * is a user fails with `permission-denied`.
   */
  private typeCatalog$(): Observable<Map<string, NotificationTypeCatalogItem>> {
    return this.firestoreService.doc$<NotificationTypeCatalog>(NOTIFICATION_TYPES_DOC).pipe(
      map((config) => this.toCatalogMap(config)),
      catchError((error) => {
        console.error('notifications: type catalog unavailable; tiles will show placeholders', error);

        return of(new Map<string, NotificationTypeCatalogItem>());
      }),
      startWith(new Map<string, NotificationTypeCatalogItem>())
    );
  }

  /**
   * Entries without a usable `key` and `initials` are dropped rather than trusted: this document is
   * hand-edited, and a row with a numeric key or a missing tile would otherwise reach the template.
   */
  private toCatalogMap(config: NotificationTypeCatalog | undefined | null): Map<string, NotificationTypeCatalogItem> {
    const entries = new Map<string, NotificationTypeCatalogItem>();
    if (!Array.isArray(config?.catalog)) {
      return entries;
    }

    config.catalog.forEach((item) => {
      const key = typeof item?.key === 'string' ? item.key.trim() : '';
      const initials = typeof item?.initials === 'string' ? item.initials.trim() : '';
      if (key && initials) {
        entries.set(key, { key, initials, label: typeof item.label === 'string' ? item.label : undefined });
      }
    });

    return entries;
  }

  /** Firestore Timestamps only have `toDate()` once they are real; a pending write has none yet. */
  private toDate(value: unknown): Date | null {
    const timestamp = value as firebase.firestore.Timestamp | undefined;

    return timestamp && typeof timestamp.toDate === 'function' ? timestamp.toDate() : null;
  }

  /**
   * Today / Yesterday / Earlier, by calendar day in the reader's own timezone.
   *
   * Calendar day, not "within 24 hours": a notification from 11pm last night belongs under
   * Yesterday at 9am today, and a rolling window would file it under Today.
   */
  private groupOf(date: Date | null): NotificationRow['group'] {
    if (!date) {
      return 'Earlier';
    }

    const startOfToday = new Date();
    startOfToday.setHours(0, 0, 0, 0);

    if (date >= startOfToday) {
      return 'Today';
    }

    const startOfYesterday = new Date(startOfToday);
    startOfYesterday.setDate(startOfYesterday.getDate() - 1);

    return date >= startOfYesterday ? 'Yesterday' : 'Earlier';
  }
}

/** Shown when the catalog does not name a type — a row with a vague tile beats no row. */
const PLACEHOLDER_INITIALS = '••';
